/**
 * Hitungan geometri untuk crossplot dan histogram.
 *
 * Semuanya murni dan tanpa React: seleksi poligon, garis regresi yang dipotong
 * ke tepi kotak, kotak-kotak batang histogram, dan sel-sel peta kerapatan. Dua
 * layar memakainya, dan keduanya harus menjawab sama — sebuah titik yang
 * terhitung "di dalam" pada panel seleksi crossplot dan "di luar" pada
 * ringkasannya adalah dua gambar yang tidak berbicara tentang hal yang sama.
 *
 * Yang dipakai di sini adalah ruang nilai, bukan piksel. Pemanggilnya yang tahu
 * seberapa besar kotaknya; fungsi-fungsi ini hanya tahu di mana sebuah angka
 * berada di antara dua ujung sumbunya.
 */

export interface Vertex {
  /** Kedudukan mendatar dalam kotak gambar, 0..1 dari kiri. */
  x: number;
  /** Kedudukan tegak dalam kotak gambar, 0..1 dari atas. */
  y: number;
}

/**
 * Apakah sebuah titik berada di dalam poligon — pancaran sinar ke kanan.
 *
 * Cara ini benar juga untuk poligon cekung, dan cekung adalah bentuk yang
 * sebenarnya dipakai orang: seleksi sebuah awan hampir tidak pernah berupa
 * kotak, melainkan lidah panjang yang mengikuti kaki sebaran.
 *
 * Sisi diuji dengan setengah terbuka (`y0 > py` berbanding `y1 > py`) supaya
 * sebuah titik yang tepat sejajar dengan simpul tidak terhitung dua kali dan
 * lalu terbaca sebagai di luar.
 *
 * Yang di sini bekerja di **ruang layar** — y menaik ke bawah, dan pada sumbu
 * densitas terbalik sekali lagi — sedangkan cerminannya `petro.crossplot.
 * in_polygon` bekerja di ruang nilai. Pencerminan tidak mengubah jawaban bagi
 * titik yang benar-benar di dalam, tetapi ia membalik ujung mana yang tertutup:
 * sisi yang membentang `[y_bawah, y_atas)` di satu ruang membentang
 * `(y_bawah, y_atas]` di ruang satunya. Sebuah sampel yang nilainya **persis**
 * sama dengan y sebuah simpul karena itu bisa dihitung masuk di layar dan tidak
 * masuk di kurva yang terbit. Dibiarkan begitu dengan sengaja: simpul lahir
 * dari klik, sampel lahir dari alat, dan kesamaan persis di antara keduanya
 * praktis tidak terjadi — sedangkan menyeragamkannya menuntut salah satu dari
 * dua cerminan ini berhenti menjadi cerminan yang bisa dibaca berdampingan.
 */
export function pointInPolygon(px: number, py: number, polygon: Vertex[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const { x: xi, y: yi } = polygon[i];
    const { x: xj, y: yj } = polygon[j];
    if (yi > py !== yj > py) {
      const cut = ((xj - xi) * (py - yi)) / (yj - yi || Number.EPSILON) + xi;
      if (px < cut) inside = !inside;
    }
  }
  return inside;
}

/** Poligon sebagai jalur SVG; tertutup hanya bila memang sudah ditutup. */
export function polygonPath(polygon: Vertex[], closed: boolean, scale = 100): string {
  if (!polygon.length) return "";
  const parts = polygon.map(
    (v, i) => `${i === 0 ? "M" : "L"}${(v.x * scale).toFixed(3)} ${(v.y * scale).toFixed(3)}`,
  );
  return parts.join(" ") + (closed ? " Z" : "");
}

/** Sisa kotak gambar di luar sumbu, dalam satuan viewBox `0 0 100 100`. */
export const PLOT_PAD = 3;
/** Panjang sumbu di dalam kotak itu. */
export const PLOT_SPAN = 100 - PLOT_PAD * 2;

/**
 * Sebuah sumbu, sebatas yang diperlukan untuk memetakan nilai ke kotak.
 *
 * `lo` dan `hi` hidup di ruang yang **sudah** dipetakan: pada sumbu log
 * keduanya sudah berupa log10, bukan nilai kurvanya. Bentuk ini sengaja
 * dibuat serupa dengan `Axis` di layar crossplot supaya yang di sana bisa
 * diserahkan apa adanya.
 */
export interface AxisMap {
  lo: number;
  hi: number;
  log: boolean;
  invert: boolean;
}

/**
 * Simpul poligon layar menjadi nilai kurva.
 *
 * Sebuah simpul disimpan sebagai pecahan kotak gambar — 0 di tepi kiri, 1 di
 * tepi kanan — dan pecahan itu tidak berarti apa-apa begitu rentang sumbunya
 * berganti. Poligon yang disimpan apa adanya karena itu berpindah tempat
 * sendiri ketika seseorang mengetik batas sumbu yang lain, tanpa satu pun
 * tanda bahwa ia berpindah: bentuknya tetap sama, yang berubah batuan yang
 * dikurungnya. Yang disimpan harus nilai kurvanya.
 *
 * Pembalikan sumbu ikut dibatalkan di sini. Sumbu densitas menaik ke bawah,
 * jadi pecahan 0,2 dari atas adalah nilai yang besar dan bukan yang kecil —
 * dan sebuah poligon yang melupakannya akan tercermin terbalik ketika dinilai
 * ulang, mengurung awan yang justru berseberangan dengan yang dipilih orang.
 */
export function vertexToValue(v: Vertex, x: AxisMap, y: AxisMap): [number, number] {
  return [axisValue(x, (v.x * 100 - PLOT_PAD) / PLOT_SPAN),
          axisValue(y, 1 - (v.y * 100 - PLOT_PAD) / PLOT_SPAN)];
}

/** Kebalikannya: nilai kurva kembali menjadi simpul di kotak gambar. */
export function valueToVertex(vx: number, vy: number, x: AxisMap, y: AxisMap): Vertex {
  return {
    x: (PLOT_PAD + axisPlace(x, vx) * PLOT_SPAN) / 100,
    y: (PLOT_PAD + (1 - axisPlace(y, vy)) * PLOT_SPAN) / 100,
  };
}

/**
 * Berapa potong tiap sisi poligon tersimpan dibagi ketika digambar ulang.
 *
 * Hanya berarti ketika ruang simpannya berbeda dengan ruang sumbu yang sedang
 * tampil; di ruang yang sama, membagi sebuah ruas lurus menghasilkan ruas
 * lurus yang sama persis. Enam belas sudah membuat lengkungan terpanjang —
 * satu sisi yang membentang tiga dasawarsa RT — terbaca sebagai lengkungan
 * dan bukan sebagai patahan.
 */
const EDGE_STEPS = 16;

/**
 * Poligon tersimpan, digambar ulang di atas sumbu yang sedang tampil.
 *
 * Sisinya dibagi **di ruang tempat ia digambar dulu**, bukan ditarik lurus di
 * ruang yang sekarang. Perbedaannya bukan kerapian. Sebuah poligon yang
 * digambar di atas sumbu RT logaritmik punya sisi yang lurus di ruang log10,
 * dan lurus di ruang log10 adalah melengkung di ruang nilai — persis itu yang
 * dipakai `petro.crossplot.in_polygon` ketika ia menggolongkan, sebab poligon
 * membawa `log_x`/`log_y`-nya sendiri ke sana. Menggambarnya lurus di ruang
 * yang salah memajang bentuk yang **bukan** bentuk yang menggolongkan: garisnya
 * rapi, isinya lain, dan tidak ada apa pun di layar yang bisa mengabarkan
 * selisihnya.
 *
 * Sebuah bentuk yang kehilangan **satu** simpul saja memulangkan daftar kosong,
 * dan itu berlaku untuk kedua ruang sekaligus: ruang tempat ia disimpan, dan
 * ruang sumbu yang sedang tampil. Keduanya bisa menolak simpul yang sama sekali
 * berbeda — nilai tak positif tidak punya tempat pada sumbu logaritmik yang
 * mana pun — dan yang dijaga di sini adalah bahwa tidak satu pun dari keduanya
 * boleh menghilangkan simpul *sendirian*. Poligon yang kehilangan satu simpul
 * lalu tetap digambar akan menutup dirinya lewat jalan pintas yang tidak pernah
 * digambar siapa pun: bentuknya tetap rapi, isinya bukan lagi isi yang
 * digolongkan server. Hilang sama sekali lebih jujur daripada berubah diam-diam.
 */
export function shapeVertices(
  values: [number, number][],
  space: { logX: boolean; logY: boolean },
  x: AxisMap,
  y: AxisMap,
  steps = EDGE_STEPS,
): Vertex[] {
  if (values.length < 3) return [];
  const into = (value: number, log: boolean) =>
    log ? (value > 0 ? Math.log10(value) : NaN) : value;
  const outOf = (mapped: number, log: boolean) => (log ? 10 ** mapped : mapped);

  const mapped = values.map(([vx, vy]) => [into(vx, space.logX), into(vy, space.logY)]);
  if (!mapped.every((pair) => pair.every(Number.isFinite))) return [];

  const out: Vertex[] = [];
  for (let i = 0; i < mapped.length; i++) {
    const [x0, y0] = mapped[i];
    const [x1, y1] = mapped[(i + 1) % mapped.length];
    // Ujung akhir tiap sisi dilewati: ia simpul pertama sisi berikutnya, dan
    // menggambarnya dua kali menaruh dua simpul di tempat yang sama.
    for (let step = 0; step < steps; step++) {
      const t = step / steps;
      const vertex = valueToVertex(
        outOf(x0 + (x1 - x0) * t, space.logX),
        outOf(y0 + (y1 - y0) * t, space.logY),
        x,
        y,
      );
      // Ditolak sumbu yang sedang tampil, dan bukan oleh ruang simpannya.
      // Sebuah bentuk yang tersimpan di ruang linier boleh memuat NPHI yang
      // sedikit negatif — alat memang membacanya begitu — dan nilai itu tidak
      // punya tempat pada sumbu logaritmik. Membuang simpulnya sendirian
      // menyisakan bentuk yang memotong sudut; yang dipulangkan tidak ada.
      if (!Number.isFinite(vertex.x) || !Number.isFinite(vertex.y)) return [];
      out.push(vertex);
    }
  }
  return out;
}

/** Kedudukan 0..1 pada sumbunya menjadi nilai kurva. */
function axisValue(axis: AxisMap, place: number): number {
  const position = axis.invert ? 1 - place : place;
  const mapped = axis.lo + position * (axis.hi - axis.lo);
  return axis.log ? 10 ** mapped : mapped;
}

/**
 * Nilai kurva menjadi kedudukan 0..1, pembalikan sudah termasuk.
 *
 * Sengaja sebangun dengan `place()` di layar crossplot. Keduanya memang
 * menjawab pertanyaan yang sama, dan dua jawaban yang berbeda atas satu titik
 * berarti poligon yang digambar di satu tempat mengurung batuan yang lain di
 * tempat berikutnya.
 */
function axisPlace(axis: AxisMap, value: number): number {
  const mapped = axis.log ? Math.log10(value) : value;
  const position = (mapped - axis.lo) / (axis.hi - axis.lo || 1);
  return axis.invert ? 1 - position : position;
}

export interface Summary {
  n: number;
  mean: number;
  sd: number;
  min: number;
  p50: number;
  max: number;
}

/**
 * Ringkasan sederet nilai, dengan yang bukan angka dibuang lebih dulu.
 *
 * Dipakai pada titik yang *tergambar* — seleksi poligon hanya bisa menghitung
 * apa yang ada di layar — jadi pemanggilnya wajib mengatakan begitu. Ringkasan
 * seluruh data dihitung server, dari seluruh data.
 */
export function summarise(values: number[]): Summary | null {
  const finite = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!finite.length) return null;
  const n = finite.length;
  const mean = finite.reduce((sum, v) => sum + v, 0) / n;
  const variance = n > 1 ? finite.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1) : 0;
  const middle = n % 2 ? finite[(n - 1) / 2] : (finite[n / 2 - 1] + finite[n / 2]) / 2;
  return { n, mean, sd: Math.sqrt(variance), min: finite[0], p50: middle, max: finite[n - 1] };
}

/**
 * Ujung garis regresi, dipotong ke rentang sumbu mendatarnya.
 *
 * Dipotong dan bukan diperpanjang: sebuah garis yang membentang sampai tepi
 * kotak menyatakan hubungan pada nilai yang tidak ada satu pun datanya, dan
 * pada crossplot itulah tepatnya bagian yang paling mudah dipercaya orang.
 */
export function fitSegment(
  fit: { slope: number; intercept: number; x0: number; x1: number },
  lo: number,
  hi: number,
): { x1: number; y1: number; x2: number; y2: number } | null {
  const left = Math.max(Math.min(fit.x0, fit.x1), Math.min(lo, hi));
  const right = Math.min(Math.max(fit.x0, fit.x1), Math.max(lo, hi));
  if (!(right > left)) return null;
  return {
    x1: left,
    y1: fit.slope * left + fit.intercept,
    x2: right,
    y2: fit.slope * right + fit.intercept,
  };
}

export interface Bar {
  /** Tepi kiri dan kanan bin dalam satuan kurvanya. */
  from: number;
  to: number;
  /** Tinggi 0..1 terhadap batang tertinggi pada gambar ini. */
  height: number;
  value: number;
}

/**
 * Batang histogram dari tepi dan cacah.
 *
 * Tingginya dinormalkan terhadap puncak *gambar*, bukan terhadap puncak tiap
 * kelompok: dua kelompok yang masing-masing menjulang penuh setinggi kotaknya
 * terlihat sama besar padahal yang satu memuat sepuluh kali lipat sampel.
 */
export function bars(edges: number[], values: number[], peak?: number): Bar[] {
  if (edges.length < 2 || !values.length) return [];
  const top = peak ?? Math.max(...values, 0);
  return values.map((value, index) => ({
    from: edges[index],
    to: edges[index + 1] ?? edges[edges.length - 1],
    height: top > 0 ? Math.max(0, value) / top : 0,
    value,
  }));
}

export interface Cell {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  count: number;
  /** Kedudukan warna 0..1 sesudah dilembutkan. */
  t: number;
}

/**
 * Sel peta kerapatan, dalam satuan sumbunya, beserta kedudukan warnanya.
 *
 * Akar dan bukan bagi lurus. Kerapatan sebuah awan log hampir selalu berekor
 * panjang — satu sel bisa memuat lima ratus sampel sementara sebagian besar
 * sel memuat dua atau tiga — dan pembagian lurus terhadap puncak lalu
 * mengecat hampir seluruh peta dengan warna ujung terendah, yang berarti
 * peta yang tidak menunjukkan apa pun kecuali satu bintik terang.
 */
export function densityCells(grid: {
  bins: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  counts: number[][];
  max: number;
}): Cell[] {
  const cells: Cell[] = [];
  const dx = (grid.x1 - grid.x0) / grid.bins;
  const dy = (grid.y1 - grid.y0) / grid.bins;
  const peak = grid.max || 1;
  for (let row = 0; row < grid.counts.length; row++) {
    for (let col = 0; col < grid.counts[row].length; col++) {
      const count = grid.counts[row][col];
      if (count <= 0) continue;
      cells.push({
        x0: grid.x0 + col * dx,
        x1: grid.x0 + (col + 1) * dx,
        y0: grid.y0 + row * dy,
        y1: grid.y0 + (row + 1) * dy,
        count,
        t: Math.sqrt(count / peak),
      });
    }
  }
  return cells;
}

export interface Marginals {
  /** Cacah per kolom, sepanjang sumbu mendatar. */
  x: number[];
  /** Cacah per baris, sepanjang sumbu tegak. */
  y: number[];
  xMax: number;
  yMax: number;
}

/**
 * Histogram tepi kedua sumbu, dijumlahkan dari peta kerapatan.
 *
 * Diturunkan dari grid dan **bukan** dihitung ulang dari titik yang tergambar,
 * dan itu bukan penghematan melainkan syarat kebenarannya. Awan yang dikirim ke
 * layar sudah dijarangkan menjadi enam ribu titik dari berapa pun aslinya;
 * sebuah histogram yang dihitung darinya melaporkan bentuk yang benar dengan
 * tinggi yang salah — dan tinggi itulah yang dibaca orang sebagai "berapa
 * banyak batuan di selang ini". Gridnya sendiri dihitung server atas seluruh
 * sampel, jadi menjumlahkan barisnya mewarisi sifat itu utuh.
 *
 * Menjumlahkan grid juga berarti kedua histogram berdiri di atas bin yang sama
 * persis dengan peta kerapatan di sebelahnya, sampai ke tepinya. Dua gambar
 * yang berbicara tentang sampel yang sama tetapi membaginya dengan dua cara
 * yang berbeda adalah dua gambar yang tidak bisa dibandingkan dengan mata —
 * dan keduanya berdampingan di satu kotak.
 */
export function marginals(grid: { bins: number; counts: number[][] }): Marginals {
  const x = new Array<number>(grid.bins).fill(0);
  const y = new Array<number>(grid.counts.length).fill(0);
  for (let row = 0; row < grid.counts.length; row++) {
    for (let col = 0; col < grid.counts[row].length; col++) {
      const n = grid.counts[row][col];
      if (n <= 0) continue;
      y[row] += n;
      // Baris grid bisa lebih pendek daripada `bins` pada muatan yang cacat;
      // yang di luar dibuang alih-alih memperpanjang larik diam-diam, sebab
      // larik yang lebih panjang daripada sumbunya menggambar batang di luar
      // kotaknya.
      if (col < x.length) x[col] += n;
    }
  }
  return {
    x,
    y,
    xMax: x.reduce((top, n) => Math.max(top, n), 0),
    yMax: y.reduce((top, n) => Math.max(top, n), 0),
  };
}

/**
 * Warna deret ke-n untuk pewarnaan menurut sumur atau zona.
 *
 * Dipakai hanya bila bendanya sendiri tidak membawa warna. Sumur dan zona
 * menyimpan warnanya di server justru supaya lembar korelasi, peta dan
 * crossplot menyebut sumur yang sama dengan warna yang sama; daftar ini
 * untuk yang belum pernah diberi warna oleh siapa pun.
 */
export const SERIES_COLORS = [
  "#35b8c6", "#d69a4a", "#7fc06a", "#b47bd0", "#e0685f",
  "#4f8fd0", "#c9b73f", "#59b39a", "#d071a2", "#8c8f99",
];

export function seriesColor(index: number, given?: string): string {
  return isColour(given) ? given! : SERIES_COLORS[index % SERIES_COLORS.length];
}

/** Sebuah nilai yang benar-benar warna, dan bukan "none" atau string kosong. */
function isColour(value?: string): boolean {
  return typeof value === "string" && /^#[0-9a-f]{3,8}$/i.test(value.trim());
}

/**
 * Warna yang bisa dibedakan untuk sederet benda yang membawa warnanya sendiri.
 *
 * Warna sumur di aplikasi ini berasal dari statusnya — minyak hijau, gas
 * kuning, kering kelabu — dan itu benar di peta, tempat yang ditanyakan orang
 * memang "yang mana yang kering". Pada crossplot yang diwarnai *menurut sumur*,
 * aturan yang sama menghasilkan tiga sumur minyak dengan satu hijau yang sama:
 * legenda yang menyebut tiga nama untuk satu warna tidak menyebut apa pun. Dan
 * sumur rencana membawa warna "none", yang digambar sebagai titik tak terlihat.
 *
 * Karena itu: yang warnanya sah dan belum terpakai mempertahankannya — supaya
 * sumur yang sama tetap sewarna dengan lembar korelasi dan peta — dan yang
 * bertabrakan atau tidak berwarna mengambil warna berikutnya dari daftar.
 */
export function distinctColors(given: (string | undefined)[]): string[] {
  const used = new Set<string>();
  const out: string[] = [];
  for (const value of given) {
    const own = isColour(value) ? value!.toLowerCase() : null;
    if (own && !used.has(own)) {
      used.add(own);
      out.push(own);
      continue;
    }
    const spare = SERIES_COLORS.find((color) => !used.has(color.toLowerCase()));
    const chosen = spare ?? SERIES_COLORS[out.length % SERIES_COLORS.length];
    used.add(chosen.toLowerCase());
    out.push(chosen);
  }
  return out;
}
